/**
 * Region-aware "now" helpers for call logging.
 *
 * Backend stores every timestamp in UTC ISO — this utility only affects how
 * we DISPLAY the current moment when an agent logs a call, plus what strings
 * we submit to the server. The backend converts the region-local wall-clock
 * back to UTC before writing (see routes/calls.py manual-log).
 *
 * India   → Asia/Kolkata (IST, UTC+5:30)
 * Bangladesh → Asia/Dhaka (BDT, UTC+6:00)
 * Anything else falls back to India (IST) — Locofast operates only in these two.
 */

const REGION_TZ = {
  India: { zone: 'Asia/Kolkata', label: 'IST' },
  Bangladesh: { zone: 'Asia/Dhaka', label: 'BDT' },
};

const DEFAULT = REGION_TZ.India;

export function regionTz(region) {
  return REGION_TZ[region] || DEFAULT;
}

/**
 * Returns the current wall-clock in the lead's local timezone as
 * `{ date: 'YYYY-MM-DD', time: 'HH:MM', label: 'IST' | 'BDT', zone: 'Asia/...' }`.
 */
export function getRegionNow(region) {
  const { zone, label } = regionTz(region);
  const now = new Date();
  // `sv-SE` gives ISO-like `YYYY-MM-DD HH:mm:ss` output regardless of user locale.
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
  return {
    date: `${map.year}-${map.month}-${map.day}`,
    time: `${map.hour}:${map.minute}`,
    label,
    zone,
  };
}
